// Small math helpers. The sim runs on the ground plane using (x, z); render
// maps that to Three.js (x, height=y, z).

export const TAU = Math.PI * 2

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

/** Frame-rate independent exponential smoothing factor. */
export function damp(current: number, target: number, lambda: number, dt: number): number {
  return lerp(current, target, 1 - Math.exp(-lambda * dt))
}

export function dist2(ax: number, az: number, bx: number, bz: number): number {
  const dx = ax - bx
  const dz = az - bz
  return dx * dx + dz * dz
}

export function dist(ax: number, az: number, bx: number, bz: number): number {
  return Math.sqrt(dist2(ax, az, bx, bz))
}

/** Returns normalized (x,z) pointing from a to b, plus the distance. */
export function dirTo(
  ax: number,
  az: number,
  bx: number,
  bz: number,
): { x: number; z: number; len: number } {
  const dx = bx - ax
  const dz = bz - az
  const len = Math.hypot(dx, dz)
  if (len < 1e-6) return { x: 0, z: 0, len: 0 }
  return { x: dx / len, z: dz / len, len }
}

export function angleOf(x: number, z: number): number {
  return Math.atan2(z, x)
}

export function fromAngle(a: number): { x: number; z: number } {
  return { x: Math.cos(a), z: Math.sin(a) }
}

/** Shortest signed angular difference a->b in radians. */
export function angleDelta(a: number, b: number): number {
  let d = (b - a) % TAU
  if (d < -Math.PI) d += TAU
  if (d > Math.PI) d -= TAU
  return d
}

export function approach(current: number, target: number, maxDelta: number): number {
  if (current < target) return Math.min(current + maxDelta, target)
  return Math.max(current - maxDelta, target)
}
